import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile';
  twitterCard?: 'summary' | 'summary_large_image';
  noIndex?: boolean;
  structuredData?: any;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = [],
  canonicalUrl,
  ogImage = './assets/branding/og-image.svg',
  ogType = 'website',
  twitterCard = 'summary',
  noIndex = false,
  structuredData
}) => {
  const siteName = '好野貸 - 專業借貸服務平台';
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const allKeywords = [
    '借貸', '貸款', '信貸', '房貸', '車貸', '企業貸款', '債務整合',
    '信用諮詢', '資金需求', '快速貸款', '低利率貸款', '好野貸',
    ...keywords
  ].join(', ');

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow'} />
      
      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Additional SEO Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#2563eb" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      <meta name="msapplication-TileImage" content="/ms-icon-144x144.png" />
      <meta name="application-name" content="好野貸" />
      
      {/* Favicons */}
      <link rel="apple-touch-icon" sizes="57x57" href="/apple-icon-57x57.png" />
      <link rel="apple-touch-icon" sizes="60x60" href="/apple-icon-60x60.png" />
      <link rel="apple-touch-icon" sizes="72x72" href="/apple-icon-72x72.png" />
      <link rel="apple-touch-icon" sizes="76x76" href="/apple-icon-76x76.png" />
      <link rel="apple-touch-icon" sizes="114x114" href="/apple-icon-114x114.png" />
      <link rel="apple-touch-icon" sizes="120x120" href="/apple-icon-120x120.png" />
      <link rel="apple-touch-icon" sizes="144x144" href="/apple-icon-144x144.png" />
      <link rel="apple-touch-icon" sizes="152x152" href="/apple-icon-152x152.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-icon-180x180.png" />
      <link rel="icon" type="image/png" sizes="192x192" href="/android-icon-192x192.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="96x96" href="/favicon-96x96.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/manifest.json" />
    </Helmet>
  );
};

// 預定義的 SEO 配置
export const homePageSEO: SEOProps = {
  title: '首頁',
  description: '好野貸提供專業借貸服務，包括個人信貸、房屋貸款、企業融資、汽車貸款、債務整合等。快速審核，優惠利率，安全可靠。立即諮詢！',
  keywords: ['首頁', '借貸服務', '快速審核', '優惠利率'],
  canonicalUrl: 'https://haoyedai.com',
  structuredData: {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    "name": "好野貸",
    "description": "專業借貸服務平台",
    "url": "https://haoyedai.com",
    "telephone": "+886-LINE-@939rzwem",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "TW",
      "addressRegion": "台灣"
    },
    "serviceType": ["個人信貸", "房屋貸款", "企業融資", "汽車貸款", "債務整合"],
    "hoursAvailable": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  }
};

export const servicesPageSEO: SEOProps = {
  title: '服務項目',
  description: '好野貸提供多種專業借貸服務：個人信貸、房屋貸款、企業融資、汽車貸款、債務整合、信用諮詢。每項服務都有專業團隊為您量身打造最適合的方案。',
  keywords: ['服務項目', '個人信貸', '房屋貸款', '企業融資', '汽車貸款', '債務整合', '信用諮詢'],
  canonicalUrl: 'https://haoyedai.com/services',
  structuredData: {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Service",
          "name": "個人信貸",
          "description": "靈活運用資金，滿足個人需求"
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Service",
          "name": "房屋貸款",
          "description": "實現購屋夢想，資金靈活運用"
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "Service",
          "name": "企業融資",
          "description": "企業成長的最佳夥伴"
        }
      }
    ]
  }
};

export const aboutPageSEO: SEOProps = {
  title: '關於我們',
  description: '了解好野貸的專業團隊、服務理念、優勢特色。我們致力於為每位客戶提供最適合的貸款方案，讓您的資金需求得到最快速、最安全的解決方案。',
  keywords: ['關於我們', '公司介紹', '服務理念', '專業團隊', '服務優勢'],
  canonicalUrl: 'https://haoyedai.com/about',
  structuredData: {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "關於好野貸",
    "description": "專業借貸服務平台介紹",
    "mainEntity": {
      "@type": "Organization",
      "name": "好野貸",
      "description": "專業借貸服務平台",
      "foundingDate": "2020",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "TW",
        "addressRegion": "台灣"
      }
    }
  }
};

export default SEO;